import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { Button } from '../common/Button';
import { 
  Map, MessageSquare, MapPin, Route, Settings, AlertTriangle,
  Ruler, Zap, Cloud, Crosshair, FileText, Users, Camera
} from 'lucide-react';

export const WelcomeOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Welcome to OverSizeHub!',
      description: 'Your all-in-one solution for route surveys. Let\'s explore the powerful features that will help you work more efficiently.',
      icon: Map,
      color: 'text-blue-500'
    },
    {
      title: 'Survey Management',
      description: 'Create, organize, and manage your surveys with ease. Track progress, add details, and maintain a complete history of your work.',
      icon: FileText,
      color: 'text-green-500',
      features: [
        'Create unlimited surveys',
        'Track survey progress',
        'Add customer information',
        'Manage vehicle details',
        'Export in multiple formats'
      ]
    },
    {
      title: 'Route Recording',
      description: 'Record your routes in real-time with advanced tracking and measurement capabilities.',
      icon: Route,
      color: 'text-yellow-500',
      features: [
        'Real-time GPS tracking',
        'Automatic route recording',
        'Manual route planning',
        'Distance calculations',
        'Turn-by-turn navigation'
      ]
    },
    {
      title: 'Points of Interest',
      description: 'Mark and document important locations along your route with detailed information and measurements.',
      icon: MapPin,
      color: 'text-red-500',
      features: [
        'Multiple POI types',
        'Photo attachments',
        'Custom measurements',
        'Location details',
        'Elevation data'
      ]
    },
    {
      title: 'Advanced Measurements',
      description: 'Use cutting-edge technology to take precise measurements of structures and obstacles.',
      icon: Ruler,
      color: 'text-purple-500',
      features: [
        'LiDAR measurements',
        'Distance calculations',
        'Height measurements',
        'Clearance checks',
        'Measurement history'
      ]
    },
    {
      title: 'Real-Time Conditions',
      description: 'Stay informed about weather and road conditions that might affect your route.',
      icon: Cloud,
      color: 'text-cyan-500',
      features: [
        'Weather forecasts',
        'Road conditions',
        'Traffic updates',
        'Construction zones',
        'Hazard alerts'
      ]
    },
    {
      title: 'Team Collaboration',
      description: 'Work together seamlessly with your team members and share information in real-time.',
      icon: Users,
      color: 'text-indigo-500',
      features: [
        'Real-time collaboration',
        'Role-based access',
        'Comments and notes',
        'Activity tracking',
        'Survey sharing'
      ]
    },
    {
      title: 'Documentation',
      description: 'Capture and organize all necessary documentation for your surveys.',
      icon: Camera,
      color: 'text-pink-500',
      features: [
        'Photo documentation',
        'Notes and comments',
        'Custom forms',
        'Document attachments',
        'Export options'
      ]
    },
    {
      title: 'Support & Help',
      description: 'Get help whenever you need it through our comprehensive support system.',
      icon: MessageSquare,
      color: 'text-emerald-500',
      showSupport: true,
      features: [
        'Live chat support',
        'Documentation center',
        'Video tutorials',
        'Email support',
        'Feature requests'
      ]
    }
  ];

  const handleComplete = async () => {
    try {
      const userRef = doc(db, 'userProfiles', auth.currentUser.uid);
      await updateDoc(userRef, {
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      });
      navigate('/');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center p-4">
      <div className="bg-[#2A2B2E] rounded-lg p-8 w-full max-w-xl">
        {/* Progress Bar */}
        <div className="flex gap-2 mb-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors ${
                index <= currentStep ? 'bg-red-500' : 'bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${currentStepData.color} bg-opacity-10`}>
              <currentStepData.icon className={`w-8 h-8 ${currentStepData.color}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{currentStepData.title}</h2>
              <p className="text-gray-400 mt-1">{currentStepData.description}</p>
            </div>
          </div>

          {/* Feature List */}
          {currentStepData.features && (
            <div className="bg-gray-800 p-4 rounded-lg">
              <ul className="space-y-2">
                {currentStepData.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-300">
                    <div className={`w-1.5 h-1.5 rounded-full ${currentStepData.color}`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Support Info */}
          {currentStepData.showSupport && (
            <div className="bg-gray-800 p-4 rounded-lg space-y-3">
              <h3 className="font-medium text-white">How to Get Support:</h3>
              <ol className="space-y-2 text-gray-300">
                <li className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span>Open Settings from the home screen</span>
                </li>
                <li className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  <span>Navigate to the Support tab</span>
                </li>
                <li className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Choose between live chat, bug report, or documentation</span>
                </li>
              </ol>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            {currentStep > 0 && (
              <Button
                variant="default"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
              >
                Back
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                if (currentStep < steps.length - 1) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  handleComplete();
                }
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Get Started'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};